import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Loader2, Check, ChevronLeft, ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d; };
const iso = (d) => d.toISOString().slice(0, 10);
const dayLabel = (d) => d.toLocaleDateString("es-MX", { weekday: "short", day: "2-digit", month: "short" });

export default function BookingDialog({ slug, primary, name, open, onOpenChange }) {
  const [offset, setOffset] = useState(0);
  const [slots, setSlots] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "" });
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const date = addDays(offset);

  useEffect(() => {
    if (!open) return;
    setSlots(null); setSelected(null);
    api.get(`/public/card/${slug}/availability?date=${iso(date)}`)
      .then(({ data }) => setSlots(data.slots)).catch(() => setSlots([]));
  }, [open, offset, slug]);

  const book = async () => {
    setSending(true);
    try {
      await api.post(`/public/card/${slug}/appointment`, { ...form, start: selected.start });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo agendar");
    } finally { setSending(false); }
  };

  const reset = () => { setStep(1); setSelected(null); setDone(false); setForm({ name: "", phone: "", email: "", service: "" }); setOffset(0); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><CalendarClock className="h-5 w-5" style={{ color: primary }} /> Agendar cita</DialogTitle>
          <DialogDescription>Con {name}</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="text-center py-6" data-testid="booking-success">
            <div className="h-12 w-12 rounded-full grid place-items-center mx-auto text-white" style={{ background: primary }}><Check className="h-6 w-6" /></div>
            <h3 className="mt-4 font-heading font-bold text-lg text-xuma-dark">¡Cita agendada!</h3>
            <p className="mt-1 text-sm text-xuma-muted">Recibirás la confirmación. ¡Nos vemos pronto!</p>
          </div>
        ) : step === 1 ? (
          <div>
            <div className="flex items-center justify-between mb-3">
              <Button variant="ghost" size="icon" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - 1))} data-testid="booking-prev-day"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm font-medium text-xuma-dark capitalize">{dayLabel(date)}</span>
              <Button variant="ghost" size="icon" disabled={offset >= 14} onClick={() => setOffset((o) => o + 1)} data-testid="booking-next-day"><ChevronRight className="h-4 w-4" /></Button>
            </div>
            {slots === null ? (
              <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-xuma-primary" /></div>
            ) : slots.length === 0 ? (
              <p className="py-10 text-center text-sm text-xuma-muted">Sin horarios disponibles este día. Prueba otro.</p>
            ) : (
              <div className="grid grid-cols-3 gap-2 max-h-56 overflow-y-auto">
                {slots.map((s) => (
                  <button key={s.start} data-testid={`slot-${s.label}`} onClick={() => setSelected(s)}
                    className={`h-10 rounded-xl text-sm font-medium border transition-colors ${selected?.start === s.start ? "text-white border-transparent" : "bg-white border-xuma-border text-xuma-dark hover:border-xuma-primary"}`}
                    style={selected?.start === s.start ? { background: primary } : {}}>
                    {s.label}
                  </button>
                ))}
              </div>
            )}
            <Button data-testid="booking-continue" disabled={!selected} onClick={() => setStep(2)}
              className="w-full mt-4 h-11 rounded-2xl text-white" style={{ background: primary }}>Continuar</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl bg-xuma-dash p-3 text-sm text-xuma-dark flex items-center gap-2">
              <CalendarClock className="h-4 w-4" style={{ color: primary }} />
              <span className="capitalize">{dayLabel(date)} · {selected?.label}</span>
            </div>
            <div><Label className="mb-1 block text-sm">Nombre *</Label><Input data-testid="booking-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-sm">Teléfono</Label><Input data-testid="booking-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label className="mb-1 block text-sm">Correo</Label><Input data-testid="booking-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <div><Label className="mb-1 block text-sm">Motivo (opcional)</Label><Input data-testid="booking-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} /></div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-2xl">Atrás</Button>
              <Button data-testid="booking-confirm" onClick={book} disabled={sending || !form.name || (!form.phone && !form.email)}
                className="flex-1 h-11 rounded-2xl text-white" style={{ background: primary }}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar cita"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
